// Internal class to group signals by knxId
class SignalGroup {
  signals = new Array<Signal>();
  channels = new Array<{ channel: Channel; number: string }>();
  knxId: string;

  private _channelText: string | undefined;

  dptDescription: any;
  knxProductConfig: KnxProductConfig;

  constructor(knxId: string, knxProductConfig: KnxProductConfig) {
    this.knxId = knxId;
    this.knxProductConfig = knxProductConfig;
  }

  // Proxy properties from first signal

  get number() {
    const numbers = this.signals.map((s) => Number(s.number || "-1")).sort();

    return numbers.join(", ");
  }

  get channelText(): string {
    if (this._channelText != undefined) {
      return this._channelText;
    }

    if (this.channels.length == 1) {
      return this.channels[0].channel.name;
    }

    const channels = this.channels.map((c) => `${c.channel.name}:${c.number}`).sort();
    this._channelText = channels.join(", ");
    return this._channelText || "";
  }

  get knxTextEN() {
    return this.signals[0]?.knxTextEN;
  }
  get knxFunctionText() {
    return this.signals[0]?.knxFunctionText;
  }
  get knxDatapointType() {
    return this.signals[0]?.knxDatapointType;
  }

  get typeName(): string {
    const typeFromConfig = this.knxProductConfig.getSignalTypeNameMapping(this.knxId);
    if (typeFromConfig) {
      return typeFromConfig;
    }

    return this.signals[0]?.type.name;
  }

  set typeName(value: string) {
    // "automatisch_def" means: clear the manual override and fall back to auto detection
    this.knxProductConfig.setSignalTypeNameMapping(this.knxId, value === "automatisch_def" ? undefined : value);
  }

  get determinedFromId() {
    const typeFromConfig = this.knxProductConfig.getSignalTypeNameMapping(this.knxId);
    if (typeFromConfig) return "manual";

    return this.signals[0]?.determinedFromId;
  }
  get name() {
    return this.signals[0]?.name;
  }
}

import React, { useEffect, useMemo, useRef, useState } from "react";
import { KnxMasterData } from "../KnxProductDataTools/LoadKnxMasterData";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import Logger from "../Logger";
import { showToast } from "./Toasts";
import Device from "../models/Device";
import { itx, noItx } from "../translationItx/itx";

import SignalTypes, { getSignalTypeByName } from "../models/SignalTypes";
import SignalAnalyzer, { Condition } from "../models/SignalAnalyzer";

import DataGrid, { Column, RenderHeaderCellProps, SortColumn, SelectColumn } from "react-data-grid";
import "react-data-grid/lib/styles.css";
import Signal from "../models/Signal";
import Channel from "../models/Channel";
import SelectSignalTypeDialog from "../modelDialogs/SelectSignalTypeDialog";
import determineActorComObjectFunction from "../KnxProductDataTools/determineActorComObjectFunction";
import determineButtonComObjectFunction from "../KnxProductDataTools/determineButtonComObjectFunction";

import { KnxProductConfig } from "../KnxProductConfig";
import ProductStorageHandler, { type ProductConfigGitlabOrderFolderAssets } from "../persistence/ProductStorageHandle";
import { cleanDecode, downloadFile } from "../utils/CommonUtils";
import { template } from "handlebars";

const logger = new Logger();

// Filter interface for Signal columns
interface SignalFilter {
  number: string;
  knxTextEN: string;
  knxFunctionText: string;
  knxDatapointType: string;
  knxId: string;
  typeName: string;
  determinedFromId: string;
  manual: string;
  dptDescription: string;
  channelText: string;
  enabled: boolean;
}

// Track which filters use exact (*) matching vs implicit substring matching
interface ExactMatchMode {
  number: boolean;
  knxTextEN: boolean;
  knxFunctionText: boolean;
  knxDatapointType: boolean;
  knxId: boolean;
  typeName: boolean;
  determinedFromId: boolean;
  manual: boolean;
  dptDescription: boolean;
  channelText: boolean;
}

const FilterContext = React.createContext<SignalFilter | undefined>(undefined);
const ExactMatchContext = React.createContext<
  { exactMatch: ExactMatchMode; setExactMatch: React.Dispatch<React.SetStateAction<ExactMatchMode>> } | undefined
>(undefined);

type GitlabUploadKind = "allSignals" | "pdf" | "applicationXml";

type GitlabUploadState = {
  kind: GitlabUploadKind;
  fileName: string;
  percentage: number;
  message: string;
  completed: boolean;
  isError?: boolean;
};

function inputStopPropagation(event: React.KeyboardEvent<HTMLInputElement>) {
  if (["ArrowLeft", "ArrowRight", " "].includes(event.key)) {
    event.stopPropagation();
  }
}

function FilterRenderer<R>({
  tabIndex,
  column,
  sortDirection,
  children,
}: RenderHeaderCellProps<R> & {
  children: (args: { tabIndex: number; filters: SignalFilter }) => React.ReactElement;
}) {
  const filters = React.useContext(FilterContext)!;
  return (
    <>
      <div style={{ cursor: "pointer" }}>
        {column.name}
        {sortDirection === "ASC" && <span style={{ float: "right" }}>⮝</span>}
        {sortDirection === "DESC" && <span style={{ float: "right" }}>⮟</span>}
      </div>
      {filters.enabled && <div>{children({ tabIndex, filters })}</div>}
    </>
  );
}

class ParamFilter {
  condition: Condition;
  exclude: boolean;
  constructor(condition: Condition, exclude = true) {
    this.condition = condition;
    this.exclude = exclude;
  }

  apply(signalAnalyzer: Array<SignalAnalyzer>): Array<SignalAnalyzer> {
    if (this.exclude) {
      return signalAnalyzer.filter((signalAnalyzer) =>
        signalAnalyzer.parameters.every((conditions) =>
          conditions.every((condition) => !(this.condition.toString() == condition.toString())),
        ),
      );
    } else {
      return signalAnalyzer.filter((signalAnalyzer) =>
        signalAnalyzer.parameters.some((conditions) =>
          conditions.some((condition) => this.condition.toString() == condition.toString()),
        ),
      );
    }
  }

  toString(): string {
    return `${this.exclude ? "-" : "+"}${this.condition.toString()}`;
  }
}

export default function AllSignalsOfTemplate(props: { device: Device; isActor: boolean }) {
  const { device, isActor } = props;

  const template = device.template;

  const [knxMasterData, setKnxMasterData] = useState<KnxMasterData | undefined>(undefined);
  const mountedRef = useRef(false);

  const [signalGroups, setSignalGroups] = useState<SignalGroup[]>([]);
  const [totalSignalCount, setTotalSignalCount] = useState(0);
  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([{ columnKey: "number", direction: "ASC" }]);
  const [selectedRows, setSelectedRows] = useState<ReadonlySet<string>>(() => new Set());
  const [uploadingAllSignals, setUploadingAllSignals] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingApplicationXml, setUploadingApplicationXml] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [gitlabAssets, setGitlabAssets] = useState<ProductConfigGitlabOrderFolderAssets | undefined>(undefined);
  const [gitlabAssetsLoading, setGitlabAssetsLoading] = useState(false);
  const [gitlabAssetsError, setGitlabAssetsError] = useState("");
  const [gitlabUpload, setGitlabUpload] = useState<GitlabUploadState | undefined>(undefined);
  const [filters, setFilters] = useState<SignalFilter>({
    number: "",
    knxTextEN: "",
    knxFunctionText: "",
    knxDatapointType: "",
    knxId: "",
    typeName: "",
    determinedFromId: "",
    manual: "",
    dptDescription: "",
    channelText: "",
    enabled: true,
  });
  const [exactMatch, setExactMatch] = useState<ExactMatchMode>({
    number: false,
    knxTextEN: false,
    knxFunctionText: false,
    knxDatapointType: false,
    knxId: false,
    typeName: false,
    determinedFromId: false,
    manual: false,
    dptDescription: false,
    channelText: false,
  });

  // Extracted: src/modelDialogs/SelectSignalTypeDialog.tsx

  // Wildcard pattern matching: * matches any characters
  // If exactMode is false, implicit *pattern* is added
  function wildcardMatch(pattern: string, text: string, exactMode: boolean): boolean {
    if (!pattern || pattern === "*") return true;
    if (!text) return false;

    let adjustedPattern = pattern;
    if (!exactMode) {
      // Add implicit * at start/end if not present (for substring matching)
      if (!adjustedPattern.startsWith("*")) {
        adjustedPattern = "*" + adjustedPattern;
      }
      if (!adjustedPattern.endsWith("*")) {
        adjustedPattern = adjustedPattern + "*";
      }
    }

    // Escape regex special chars except *, then replace * with .*
    const regexPattern = adjustedPattern
      .toLowerCase()
      .replace(/[.+?^${}()|[\]\\]/g, "\\$&") // escape special regex chars  // no itx
      .replace(/\*/g, ".*"); // replace * with .*  // no itx

    try {
      const regex = new RegExp(`^${regexPattern}$`, "i");
      return regex.test(text);
    } catch {
      return false;
    }
  }

  useOnChangeWatcher(state.settings);

  function getDptDescription(knxDatapointType: string): string {
    if (knxMasterData && knxDatapointType) {
      const dpt = knxMasterData.dpts.get(knxDatapointType);
      return dpt?.Text || dpt?.Name || "";
    }
    return "";
  }

  async function init() {
    try {
      const masterData = await state.loadKnxMasterData();
      if (!mountedRef.current) return;
      setKnxMasterData(masterData);

      if (template) {
        template.createDummyDevice();
        const templateSignals: Signal[] = await template.knxProductData.allSignals(template.dummyDevice!);
        if (!mountedRef.current) return;

        // Re-apply config overrides and auto-detection on cached signal objects (mirrors SignalAnalyzer.allSignalAnalyzers)
        for (const sig of templateSignals) {
          const configType = template.knxProductData.config.getSignalTypeMapping(sig.knxId);
          if (configType) {
            sig.type = configType;
            sig.determinedFromId = "manual";
          } else if (isActor) {
            determineActorComObjectFunction(sig);
          } else {
            determineButtonComObjectFunction(sig);
          }
        }

        // Group signals by knxId (jedes Signal einzeln zuweisen)
        const groupsMap = new Map<string, SignalGroup>();

        for (const sig of templateSignals) {
          sig.knxId ||= "???";

          let group = groupsMap.get(sig.knxId);
          if (!group) {
            group = new SignalGroup(sig.knxId, template.knxProductData.config);
            group.dptDescription = getDptDescription(sig.knxDatapointType);
            // Use knxMasterData lookup for both
            groupsMap.set(sig.knxId, group);
          }
          group.signals.push(sig);
        }

        let sCnt = 0;
        for (const knxChannel of device.knxChannels) {
          for (const sig of knxChannel.signals) {
            sCnt++;
            let group = groupsMap.get(sig.knxId);
            if (!group) {
              group = new SignalGroup(sig.knxId, template.knxProductData.config);
              group.dptDescription = getDptDescription(sig.knxDatapointType);

              groupsMap.set(sig.knxId, group);
            }

            group.channels.push({ channel: knxChannel, number: sig.number });
          }
        }

        if (mountedRef.current) {
          setSignalGroups([...groupsMap.values()]);
          setTotalSignalCount(sCnt);
        }
      }
    } catch (error) {
      logger.error(error);
    }
  }

  useEffect(() => {
    mountedRef.current = true;
    void init();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Filter rows based on filter values
  const filteredRows = useMemo(() => {
    return signalGroups.filter((group) => {
      const typeString = group.typeName;

      const manualStatus = group.determinedFromId === "manual" ? "1" : "0";
      const manualFilter = filters.manual || "";

      // manualFilter: "1" = only manual, "0" = only not manual, "" = all
      const manualMatch =
        !manualFilter ||
        (manualFilter === "1" && manualStatus === "1") ||
        (manualFilter === "0" && manualStatus === "0");

      // DPT description lookup
      let dptDescription = "";
      if (knxMasterData && group.knxDatapointType) {
        const dpt = knxMasterData.dpts.get(group.knxDatapointType);
        dptDescription = dpt?.Text || dpt?.Name || "";
      }

      return (
        wildcardMatch(filters.number, group.number || "", exactMatch.number) &&
        wildcardMatch(filters.knxTextEN, group.knxTextEN || "", exactMatch.knxTextEN) &&
        wildcardMatch(filters.knxFunctionText, group.knxFunctionText || "", exactMatch.knxFunctionText) &&
        wildcardMatch(filters.knxDatapointType, group.knxDatapointType || "", exactMatch.knxDatapointType) &&
        wildcardMatch(filters.knxId, group.knxId || "", exactMatch.knxId) &&
        wildcardMatch(filters.typeName, typeString, exactMatch.typeName) &&
        wildcardMatch(filters.determinedFromId, group.determinedFromId || "", exactMatch.determinedFromId) &&
        manualMatch &&
        wildcardMatch(filters.dptDescription, dptDescription, exactMatch.dptDescription) &&
        wildcardMatch(filters.channelText, group.channelText, exactMatch.channelText)
      );
    });
  }, [signalGroups, filters, exactMatch, knxMasterData]);

  // Sort rows based on sort columns
  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;
    const { columnKey, direction } = sortColumns[0];

    const sorted = [...filteredRows].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (columnKey === "number") {
        aValue = parseInt(a.number, 10) || 0;
        bValue = parseInt(b.number, 10) || 0;
        return aValue - bValue;
      } else if (columnKey === "manual") {
        aValue = a.determinedFromId === "manual" ? 1 : 0;
        bValue = b.determinedFromId === "manual" ? 1 : 0;
        return aValue - bValue;
      } else if (columnKey === "dptDescription") {
        // Use knxMasterData lookup for both
        const getDesc = (row: SignalGroup) => {
          if (knxMasterData && row.knxDatapointType) {
            const dpt = knxMasterData.dpts.get(row.knxDatapointType);
            return dpt?.Text || dpt?.Name || "";
          }
          return "";
        };
        aValue = getDesc(a);
        bValue = getDesc(b);
      } else {
        aValue = (a as any)[columnKey] || "";
        bValue = (b as any)[columnKey] || "";
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return aValue.localeCompare(bValue);
      }

      return 0;
    });

    return direction === "DESC" ? sorted.reverse() : sorted;
  }, [filteredRows, sortColumns, knxMasterData]);

  function csvCell(value: any): string {
    const text = String(value ?? "")
      .replace(/\r?\n/g, " ")
      .trim();
    return /[",;\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  }

  function allSignalsCsvContent(): string {
    const headers = [
      "No.", // no itx
      "Text", // no itx
      "Function", // no itx
      "TypeName", // no itx
      "manual", // no itx
      "DPT", // no itx
      "DPT Text", // no itx
      "Channels", // no itx
      "KnxId", // no itx
      "determineID", // no itx
    ];
    const rows = sortedRows.map((row) => [
      row.number,
      row.knxTextEN,
      row.knxFunctionText,
      row.typeName,
      row.determinedFromId === "manual" ? "true" : "false",
      row.knxDatapointType,
      getDptDescription(row.knxDatapointType),
      row.channelText,
      row.knxId,
      row.determinedFromId,
    ]);

    return [headers, ...rows].map((row) => row.map(csvCell).join(";")).join("\n") + "\n";
  }

  function productGitlabTarget() {
    const productInfo = template?.knxProductData?.productInfo;
    const manufacturer = cleanDecode(productInfo?.ManufacturerName || productInfo?.ManufacturerId || "");
    const orderNumber = cleanDecode(productInfo?.OrderNumber || "");
    const rawCatalogId = cleanDecode(productInfo?.CatalogId || template?.knxProductData?.modelDataId || "");
    const catalogId = rawCatalogId.replace(/^config-(?:2-)?/i, "");
    const configId = catalogId ? (catalogId.startsWith("config-") ? catalogId : `config-2-${catalogId}`) : "";
    return { manufacturer, orderNumber, catalogId, configId };
  }

  const gitlabTarget = productGitlabTarget();

  useEffect(() => {
    let cancelled = false;

    setGitlabAssets(undefined);
    setGitlabAssetsError("");
    if (!gitlabTarget.manufacturer || !gitlabTarget.orderNumber || !gitlabTarget.configId) {
      setGitlabAssetsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setGitlabAssetsLoading(true);
    ProductStorageHandler.getOrderFolderGitlabAssets(
      gitlabTarget.manufacturer,
      gitlabTarget.orderNumber,
      gitlabTarget.configId,
    )
      .then((assets) => {
        if (cancelled) return;
        if (assets?.status === "synced") {
          setGitlabAssets(assets);
        } else {
          setGitlabAssetsError(assets?.lastError || noItx("GitLab asset status could not be loaded"));
        }
      })
      .catch(() => {
        if (!cancelled) setGitlabAssetsError(noItx("GitLab asset status could not be loaded"));
      })
      .finally(() => {
        if (!cancelled) setGitlabAssetsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [gitlabTarget.manufacturer, gitlabTarget.orderNumber, gitlabTarget.configId]);

  function refreshGitlabAssets() {
    if (!gitlabTarget.manufacturer || !gitlabTarget.orderNumber || !gitlabTarget.configId) return;
    setGitlabAssetsLoading(true);
    setGitlabAssetsError("");
    ProductStorageHandler.getOrderFolderGitlabAssets(
      gitlabTarget.manufacturer,
      gitlabTarget.orderNumber,
      gitlabTarget.configId,
    )
      .then((assets) => {
        if (assets?.status === "synced") {
          setGitlabAssets(assets);
        } else {
          setGitlabAssetsError(assets?.lastError || noItx("GitLab asset status could not be loaded"));
        }
      })
      .catch(() => setGitlabAssetsError(noItx("GitLab asset status could not be loaded")))
      .finally(() => setGitlabAssetsLoading(false));
  }

  function assertGitlabTarget():
    | { manufacturer: string; orderNumber: string; configId: string; catalogId: string }
    | undefined {
    const target = productGitlabTarget();
    if (!target.manufacturer || !target.orderNumber || !target.configId || !target.catalogId) {
      showToast({
        text: noItx("Missing manufacturer, order number or config version for GitLab upload"),
        isError: true,
      });
      return undefined;
    }
    return target;
  }

  function downloadAllSignalsCsv() {
    const target = productGitlabTarget();
    const fileName = `${target.orderNumber || "allSignals"}-allSignals`;
    const blob = new Blob([allSignalsCsvContent()], {
      type: "text/csv;charset=utf-8",
    });
    downloadFile(blob, fileName, "csv");
  }

  async function downloadApplicationXml() {
    const target = productGitlabTarget();
    if (!template || !target.catalogId) {
      showToast({ text: noItx("Application XML is not available for this product"), isError: true });
      return;
    }

    try {
      const applicationXml = template.knxProductData.xmlstring || (await template.knxProductData.loadXmlstring()) || "";
      if (!applicationXml.trim()) {
        showToast({ text: noItx("Application XML is not available for this product"), isError: true });
        return;
      }

      downloadFile(new Blob([applicationXml], { type: "application/xml;charset=utf-8" }), target.catalogId, "xml");
    } catch (error) {
      showToast({
        text: error instanceof Error ? error.message : noItx("Application XML download failed"),
        isError: true,
      });
    }
  }

  async function uploadAllSignalsToGitlab() {
    const target = assertGitlabTarget();
    if (!target) return;

    setUploadingAllSignals(true);
    setGitlabUpload({
      kind: "allSignals", // no itx
      fileName: "allSignals.csv", // no itx
      percentage: 5,
      message: noItx(`Uploading allSignals.csv to ${target.orderNumber}...`),
      completed: false,
    });
    try {
      const result = await ProductStorageHandler.uploadAllSignalsToGitlab({
        ...target,
        csvContent: allSignalsCsvContent(),
        fileName: "allSignals.csv", // no itx
        onUploadProgress: (event) => {
          if (!event?.total) return;
          const uploaded = Math.round((event.loaded / event.total) * 85);
          setGitlabUpload((current) =>
            current?.kind === "allSignals"
              ? { ...current, percentage: Math.max(5, Math.min(90, 5 + uploaded)) }
              : current,
          );
        },
      });

      if (result?.status === "synced") {
        const filePath =
          result.filePath || `${target.manufacturer}/${target.orderNumber}/${target.configId}/allSignals.csv`;
        const message = noItx(
          `allSignals.csv uploaded to ${target.orderNumber} config ${target.configId} folder in GitLab: ${filePath}`,
        );
        setGitlabUpload({
          kind: "allSignals", // no itx
          fileName: "allSignals.csv", // no itx
          percentage: 100,
          message,
          completed: true,
        });
        showToast({ text: message });
        refreshGitlabAssets();
      } else {
        const message = result?.lastError || noItx("allSignals GitLab upload failed");
        setGitlabUpload((current) => current && { ...current, message, completed: true, isError: true });
        showToast({
          text: message,
          isError: true,
        });
      }
    } finally {
      setUploadingAllSignals(false);
    }
  }

  function openGitlabFilePicker() {
    if (!assertGitlabTarget()) return;
    fileInputRef.current?.click();
  }

  async function uploadSelectedFileToGitlab(file?: File) {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) {
      showToast({ text: noItx("Please select a PDF file"), isError: true });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    const target = assertGitlabTarget();
    if (!target) return;

    setUploadingFile(true);
    setGitlabUpload({
      kind: "pdf", // no itx
      fileName: file.name,
      percentage: 5,
      message: noItx(`Uploading ${file.name} to ${target.orderNumber}...`),
      completed: false,
    });
    try {
      const result = await ProductStorageHandler.uploadOrderFileToGitlab({
        ...target,
        file,
        onUploadProgress: (event) => {
          if (!event?.total) return;
          const uploaded = Math.round((event.loaded / event.total) * 85);
          setGitlabUpload((current) =>
            current?.kind === "pdf" // no itx
              ? { ...current, percentage: Math.max(5, Math.min(90, 5 + uploaded)) }
              : current,
          );
        },
      });

      if (result?.status === "synced") {
        const filePath = result.filePath || `${target.manufacturer}/${target.orderNumber}/${file.name}`;
        const message = noItx(`${file.name} uploaded to ${target.orderNumber} folder in GitLab: ${filePath}`);
        setGitlabUpload({
          kind: "pdf", // no itx
          fileName: file.name,
          percentage: 100,
          message,
          completed: true,
        });
        showToast({ text: message });
        refreshGitlabAssets();
      } else {
        const message = result?.lastError || noItx("GitLab file upload failed");
        setGitlabUpload((current) => current && { ...current, message, completed: true, isError: true });
        showToast({
          text: message,
          isError: true,
        });
      }
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function uploadApplicationXmlToGitlab() {
    const target = assertGitlabTarget();
    if (!target || !template) return;

    setUploadingApplicationXml(true);
    setGitlabUpload({
      kind: "applicationXml", // no itx
      fileName: `${target.catalogId}.xml`,
      percentage: 5,
      message: noItx(`Preparing ${target.catalogId}.xml for GitLab upload...`),
      completed: false,
    });

    try {
      const applicationXml = template.knxProductData.xmlstring || (await template.knxProductData.loadXmlstring()) || "";
      if (!applicationXml.trim()) {
        const message = noItx("Application XML is not available for this product");
        setGitlabUpload((current) =>
          current?.kind === "applicationXml" ? { ...current, message, completed: true, isError: true } : current,
        );
        showToast({ text: message, isError: true });
        return;
      }

      setGitlabUpload((current) =>
        current?.kind === "applicationXml"
          ? { ...current, percentage: 25, message: noItx(`Uploading ${target.catalogId}.xml to GitLab...`) }
          : current,
      );
      const file = new File([applicationXml], `${target.catalogId}.xml`, {
        type: "application/xml",
      });
      const result = await ProductStorageHandler.uploadConfigApplicationXmlToGitlab({
        ...target,
        file,
        onUploadProgress: (event) => {
          if (!event?.total) return;
          const uploaded = Math.round((event.loaded / event.total) * 70);
          setGitlabUpload((current) =>
            current?.kind === "applicationXml"
              ? { ...current, percentage: Math.max(25, Math.min(90, 25 + uploaded)) }
              : current,
          );
        },
      });

      if (result?.status === "synced") {
        const filePath =
          result.filePath || `${target.manufacturer}/${target.orderNumber}/${target.configId}/${target.catalogId}.xml`;
        const message = noItx(`${target.catalogId}.xml uploaded to GitLab config: ${filePath}`);
        setGitlabUpload({
          kind: "applicationXml", // no itx
          fileName: `${target.catalogId}.xml`,
          percentage: 100,
          message,
          completed: true,
        });
        showToast({ text: message });
        refreshGitlabAssets();
      } else {
        const message = result?.lastError || noItx("Application XML GitLab upload failed");
        setGitlabUpload((current) => current && { ...current, message, completed: true, isError: true });
        showToast({ text: message, isError: true });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : noItx("Application XML GitLab upload failed");
      setGitlabUpload((current) => current && { ...current, message, completed: true, isError: true });
      showToast({ text: message, isError: true });
    } finally {
      setUploadingApplicationXml(false);
    }
  }

  async function onSaveMetadata() {
    if (template) {
      const res = await template.knxProductData.saveMetadataOnProductServer(template, device);
      if (res) {
        showToast({
          text: state.translate("tabGeneralTemplate.metadataSaved", "Metadata saved"),
        });
      } else {
        showToast({
          text: state.translate("tabGeneralTemplate.metadataNotSaved", "Metadata coudn`t be saved"),
        });
      }
    }
  }

  const defaultColumnProperties = {
    resizable: true,
    sortable: true,
  };

  function filterRenderer(p: any, columnKey: keyof SignalFilter) {
    return (
      <FilterRenderer<Signal> {...p}>
        {({ filters, ...rest }: { filters: SignalFilter; [key: string]: any }) => {
          const isExact = exactMatch[columnKey as keyof ExactMatchMode];
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
              <input
                {...rest}
                className="filterClass"
                style={{ flex: 1 }}
                value={String(filters[columnKey] || "")}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    [columnKey]: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
                onClick={(e) => e.stopPropagation()}
              />
              <button
                style={{
                  padding: "2px 5px",
                  fontSize: "10px",
                  cursor: "pointer",
                  width: "27px",
                  height: "27px",
                  backgroundColor: isExact ? "#4CAF50" : "#ccc",
                  color: isExact ? "white" : "black",
                  border: "1px solid #999",
                  borderRadius: "3px",
                  minWidth: "18px",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setExactMatch({
                    ...exactMatch,
                    [columnKey]: !isExact,
                  });
                }}
                title={
                  isExact
                    ? itx("AllSignalsOfTemplate.exactmodemust", "Exact mode: * must be explicit written als wildcards")
                    : itx("AllSignalsOfTemplate.likematch", "Like Match")
                }
              >
                *
              </button>
            </div>
          );
        }}
      </FilterRenderer>
    );
  }

  const selectColumn: Column<SignalGroup> = {
    ...SelectColumn,
    headerCellClass: "selectRowData", // no itx
    cellClass: "selectRowData", // no itx
  };

  const columns: Column<SignalGroup>[] = [
    selectColumn,
    {
      key: "number",
      name: (
        <span
          data-tooltip-id="tv-tooltip"
          data-tooltip-html={noItx(
            "The signal maybe be used in multiple channels. <br/> This column shows the number in the first channel.",
          )}
          data-tooltip-place="top" //no itx
        >
          {itx("AllSignalsOfTemplate.nr", "Nr")}
        </span>
      ),
      width: 80,
      renderHeaderCell: (p) => filterRenderer(p, "number"), // no itx
      ...defaultColumnProperties,
    },
    {
      key: "knxTextEN",
      name: "Text",
      width: 200,
      renderHeaderCell: (p) => filterRenderer(p, "knxTextEN"), // no itx
      ...defaultColumnProperties,
    },
    {
      key: "knxFunctionText",
      name: itx("function", "Function"),
      width: 150,
      renderHeaderCell: (p) => filterRenderer(p, "knxFunctionText"), // no itx
      ...defaultColumnProperties,
    },
    {
      key: "typeName",
      name: "TypeName",
      width: 150,
      renderHeaderCell: (p) => filterRenderer(p, "typeName"),
      renderCell: ({ row }) => (
        <div
          style={{
            // Expand background to full cell (react-data-grid adds padding/inset)
            height: "100%",
            margin: "-8px",
            padding: "8px",
            cursor: "pointer",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            backgroundColor: row.determinedFromId === "manual" ? "#fff59d" : undefined,
          }}
          onClick={(e) => {
            e.stopPropagation();
            state.dialogManager.openDialog(
              <SelectSignalTypeDialog
                title={
                  itx("AllSignalsOfTemplate.changeOneSignal", "Ändere ein Signal:") + " " + row.number + " " + row.knxId
                }
                currentTypeName={row.typeName}
                onSelectTypeName={(typeName) => {
                  row.typeName = typeName;
                  init();
                }}
              />,
              true,
            );
          }}
        >
          <span style={{ flex: "1 1 auto", minWidth: 0 }}>{row.typeName}</span>
          <i className="las la-angle-down" style={{ opacity: 0.6, fontSize: "14px", flex: "0 0 auto" }} />
        </div>
      ),
      ...defaultColumnProperties,
    },
    {
      key: "manual",
      name: itx("manual", "manual"),
      width: 80,
      renderHeaderCell: (p) => (
        <FilterRenderer<SignalGroup> {...p}>
          {({ filters }) => (
            <select
              style={{ width: "100%" }}
              value={filters.manual || ""}
              onChange={(e) => setFilters({ ...filters, manual: e.target.value })}
              onClick={(e) => e.stopPropagation()}
            >
              <option value="">{itx("AllSignalsOfTemplate.all", "All")}</option>
              <option value="1">✔</option>
              <option value="0">✗</option>
            </select>
          )}
        </FilterRenderer>
      ),
      renderCell: ({ row }) => (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <input type="checkbox" checked={row.determinedFromId === "manual"} readOnly />
        </div>
      ),
      ...defaultColumnProperties,
    },
    {
      key: "knxDatapointType",
      name: "DPT", // no itx
      width: 100,
      renderHeaderCell: (p) => filterRenderer(p, "knxDatapointType"),
      ...defaultColumnProperties,
    },
    {
      key: "dptDescription",
      name: itx("AllSignalsOfTemplate.dpttext", "DPT-Text"),
      width: 200,
      renderHeaderCell: (p) => filterRenderer(p, "dptDescription"), // no itx
      renderCell: ({ row }) => {
        if (!knxMasterData || !row.knxDatapointType) return null;
        const dpt = knxMasterData.dpts.get(row.knxDatapointType);
        return dpt?.Text || dpt?.Name || "";
      },
      ...defaultColumnProperties,
    },
    // Channel column before knxId
    {
      key: "channelText",
      name: (
        <span
          data-tooltip-id="tv-tooltip"
          data-tooltip-content={noItx("All Channels causing the signal with the current configuration")}
          data-tooltip-place="top" // no itx
        >
          {itx("AllSignalsOfTemplate.channels", "Channels")}
        </span>
      ),
      width: 180,
      renderHeaderCell: (p) => filterRenderer(p, "channelText" as any), // no itx
      renderCell: ({ row }) => {
        return row.channelText;
      },
      ...defaultColumnProperties,
    },
    {
      key: "knxId",
      name: "KnxId",
      width: 120,
      renderHeaderCell: (p) => filterRenderer(p, "knxId"),
      ...defaultColumnProperties,
    },
    {
      key: "determinedFromId",
      name: "determineID",
      width: 150,
      renderHeaderCell: (p) => filterRenderer(p, "determinedFromId"),
      ...defaultColumnProperties,
    },
  ];

  const sChange = itx("AllSignalsOfTemplate.changeT1Signals", "Ändere T1 Signale:").replace(
    "T1",
    selectedRows.size.toString(),
  );

  return (
    <div className="visisbleCheckboxInput" style={{ height: "100%", display: "flex" }}>
      <div
        style={{
          // height: "100%",
          flex: 1,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="estadaRow w10 left gap" style={{ padding: "10px", backgroundColor: "gray", color: "white" }}>
          <span>
            {sortedRows.length} / {signalGroups.length} {noItx("SignalGroups")}{" "}
          </span>
          <div style={{ width: "50px" }}></div>
          <span>
            {noItx("In current Configuration active Signals: ")} {totalSignalCount}
          </span>
        </div>

        <FilterContext.Provider value={filters}>
          <DataGrid<SignalGroup>
            className="rdg-light"
            columns={columns}
            rows={sortedRows}
            rowKeyGetter={(row) => row.knxId}
            sortColumns={sortColumns}
            onSortColumnsChange={setSortColumns}
            selectedRows={selectedRows}
            onSelectedRowsChange={(rows) => setSelectedRows(rows as ReadonlySet<string>)}
            headerRowHeight={70}
            rowHeight={40}
            style={{
              flex: 10,
              contain: "strict",
            }}
          />
        </FilterContext.Provider>

        {/* Keep actions, status, and product controls on separate wrapping rows. */}
        <div
          className="estadaRow w100 gap"
          style={{
            padding: "8px 10px",
            backgroundColor: "gray",
            color: "white",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: "8px",
            boxSizing: "border-box",
            whiteSpace: "normal",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf" // no itx
            style={{ display: "none" }}
            onChange={(event) => uploadSelectedFileToGitlab(event.currentTarget.files?.[0])}
          />
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px" }}>
            <button
              type="button"
              className="button white"
              onClick={downloadAllSignalsCsv}
              title={noItx("Download the current allSignals.csv")}
              aria-label={noItx("Download the current allSignals.csv")}
            >
              <i className="las la-download" aria-hidden="true" /> {noItx("Download allSignals")}
            </button>

            <button
              type="button"
              className="button white"
              onClick={downloadApplicationXml}
              title={noItx("Download the product application XML")}
              aria-label={noItx("Download the product application XML")}
            >
              <i className="las la-download" aria-hidden="true" /> {noItx("Download AppXml")}
            </button>

            <button
              type="button"
              className="button white"
              onClick={uploadAllSignalsToGitlab}
              disabled={uploadingAllSignals || uploadingFile || uploadingApplicationXml}
              title={noItx("Upload the current allSignals.csv to GitLab")}
            >
              <i className="las la-upload" aria-hidden="true" />
              {uploadingAllSignals ? noItx("Uploading...") : noItx("Upload allSignals to Git")}
            </button>

            <button
              type="button"
              className="button white"
              onClick={uploadApplicationXmlToGitlab}
              disabled={uploadingApplicationXml || uploadingAllSignals || uploadingFile}
              title={noItx("Upload the product application XML to GitLab")}
            >
              <i className="las la-upload" aria-hidden="true" />
              {uploadingApplicationXml ? noItx("Uploading...") : noItx("Upload AppXml to Git")}
            </button>

            <button
              type="button"
              className="button white"
              onClick={openGitlabFilePicker}
              disabled={uploadingFile || uploadingAllSignals || uploadingApplicationXml}
              title={noItx("Upload a technical PDF to GitLab")}
            >
              <i className="las la-upload" aria-hidden="true" />
              {uploadingFile ? noItx("Uploading...") : noItx("Upload Tech PDF to Git")}
            </button>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px", minWidth: 0 }}>
            <div style={{ flex: "1 1 280px", minWidth: 0, fontSize: 12, lineHeight: 1.35 }}>
              <div style={{ fontWeight: 700 }}>
                {noItx("GitLab")} ({gitlabTarget.orderNumber || noItx("unknown device")})
                {gitlabAssetsLoading && <i className="las la-spinner la-spin" style={{ marginLeft: 6 }} />}
              </div>
              {gitlabAssetsError ? (
                <div style={{ color: "#ffd0d0", overflowWrap: "anywhere" }}>{gitlabAssetsError}</div>
              ) : gitlabAssets ? (
                <div style={{ display: "flex", flexWrap: "wrap", columnGap: 12 }}>
                  <span>
                    {noItx("CSV:")} {gitlabAssets.allSignals ? noItx("available") : noItx("missing")}
                  </span>
                  <span>
                    {noItx("XML:")} {gitlabAssets.applicationXml ? noItx("available") : noItx("missing")}
                  </span>
                  <span
                    style={{ flex: "1 1 100%", minWidth: 0, overflowWrap: "anywhere" }}
                    title={gitlabAssets.pdfs.map((pdf) => pdf.fileName).join(", ")}
                  >
                    {noItx("PDFs:")}{" "}
                    {gitlabAssets.pdfs.length > 0
                      ? gitlabAssets.pdfs.map((pdf) => pdf.fileName).join(", ")
                      : noItx("missing")}
                  </span>
                </div>
              ) : !gitlabAssetsLoading ? (
                <div>{noItx("GitLab status unavailable")}</div>
              ) : null}
            </div>

            {gitlabUpload && (
              <div style={{ flex: "1 1 280px", minWidth: 0, maxWidth: 520, fontSize: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <span
                    style={{
                      color: gitlabUpload.isError ? "#ffd0d0" : undefined,
                      minWidth: 0,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {gitlabUpload.message}
                  </span>
                  <span style={{ flex: "0 0 auto" }}>{gitlabUpload.percentage}%</span>
                </div>
                <progress value={gitlabUpload.percentage} max={100} style={{ width: "100%" }} />
              </div>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: "6px" }}>
            <button
              type="button"
              className="button white"
              onClick={(e) => {
                e.stopPropagation();
                state.dialogManager.openDialog(
                  <SelectSignalTypeDialog
                    title={sChange}
                    onSelectTypeName={(typeName) => {
                      // selectedRows enthält knxId-Strings (siehe rowKeyGetter), keine SignalGroup-Objekte
                      const byKnxId = new Map(signalGroups.map((g) => [g.knxId, g]));
                      for (const knxId of selectedRows) {
                        const group = byKnxId.get(knxId);
                        if (group) {
                          group.typeName = typeName;
                        }
                      }
                      init();
                    }}
                  />,
                  true,
                );
              }}
            >
              {sChange}
            </button>

            <button type="button" className="button white" onClick={onSaveMetadata}>
              {noItx("Save metadata on product server.")}
            </button>
          </div>
        </div>
      </div>
      <div
        style={{ display: "flex", justifyContent: "center", flexDirection: "row", height: "15%", alignItems: "center" }}
      >
        {/* // const SIGNALNUMBER_TO_DEBUG = (window as any).SIGNALNUMBER_TO_DEBUG; // "11"; */}
      </div>

      {/* Type Picker Modal is opened via dialogManager */}
    </div>
  );
}
